// Explore-stats page: Federal elections + by-elections (#225).
// Sourced from the `elections` spine (general + by-elections) and
// `election_division_results` (turnout + per-seat 2CP swing). Everything is
// aggregated server-side. Flagship: sortable listing of every election with the
// largest/winning party and seats. Slices: House seats by party family over
// time, turnout trend, by-elections per decade, and the per-seat swing
// distribution at the most recent election.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, Grid } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatStackedArea, StatLine, StatBar } from './charts';

export default function Elections() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('elections');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Federal elections + by-elections"
      description={`Every federal election since Federation — ${
        charts.general || 0} general elections and ${charts.by_elections || 0} by-elections — with the winning party, seats, turnout and per-seat swing.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="federal-elections"
    >
      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>House seats by party family</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Seats won at each general election — Labor, Coalition, and Other (incl. historic
              parties and the modern crossbench).
            </Typography>
            <StatStackedArea data={charts.seats_over_time || []} xKey="year"
                             keys={['Labor', 'Coalition', 'Other']} height={340} />
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Turnout over time</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Mean House turnout per election year (%).
                </Typography>
                <StatLine data={charts.turnout_trend || []} xKey="year"
                          keys={['turnout']} height={300} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>By-elections per decade</Typography>
                <StatBar data={charts.by_elections_by_decade || []} xKey="decade"
                         yKey="count" height={300} />
              </Paper>
            </Grid>
          </Grid>

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Swing distribution{charts.swing_year ? ` — ${charts.swing_year}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Two-candidate-preferred swing toward the winning candidate, per seat (percentage points).
            </Typography>
            <StatBar data={charts.swing_distribution || []} xKey="bin" yKey="seats" height={300} />
          </Paper>
        </>
      )}
    </ExploreStatsPage>
  );
}
